/**
 * CWR 2.4 — horizontal photo gallery (mockup .sd-gallery). Omits the whole block
 * when there are no images (2.3 decision).
 *
 * Dots are PAGE-based, not one-per-photo: with small thumbnails several photos
 * share a single scroll "page", so a one-dot-per-photo strip could never light
 * past the second dot (you can't scroll the trailing shots to the start). We
 * derive the dot count from how many viewport-widths the track spans and map
 * scroll PROGRESS across them, so the last dot lights at the end of the scroll.
 *
 * Tapping a shot opens the fullscreen lightbox. The open photo index lives in a
 * URL HASH (`#gphoto-<i>`) purely as deep-link / reload state. Closing is
 * route-stable — it clears the hash in place (`replace`) and stays on the page,
 * never a history pop (an earlier history-pop approach kept falling through to
 * the catalog on real iOS). "Back" is caught by TWO complementary seams:
 *   1. the Telegram BackButton, which the open lightbox owns (DetailLightbox →
 *      useTelegramBackButton); and
 *   2. a `useBlocker` POP guard below, for when the device back instead
 *      manifests as a browser/WKWebView history pop.
 * Whichever fires, the viewer closes in place. (A native page-hide/minimize is
 * not JS-interceptable; that path would need Telegram swipe-disable.)
 *
 * The viewer comes in as `Lightbox`: the Mini App passes DetailLightbox (with the
 * Telegram BackButton, via DetailGallery), the studio website DetailLightboxView.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ComponentType } from 'react';
import { useBlocker, useLocation, useNavigate, type BlockerFunction } from 'react-router-dom';
import type { GalleryImage } from '@/api/types';
import { FocalImage } from '@/components/FocalImage';
import type { DetailLightboxProps } from './DetailLightboxView';
import styles from '../SessionDetailPage.module.css';

export function DetailGalleryView({ gallery, Lightbox }: { gallery: GalleryImage[]; Lightbox: ComponentType<DetailLightboxProps> }) {
  const navigate = useNavigate();
  const location = useLocation();
  const trackRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState(1);
  const [active, setActive] = useState(0);

  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const cw = el.clientWidth;
    if (cw <= 0) return; // unmeasured (e.g. jsdom default / not yet laid out)
    // -4px tolerance so a sub-pixel overflow doesn't fake a second page.
    const pageCount = Math.max(1, Math.ceil((el.scrollWidth - 4) / cw));
    const maxScroll = el.scrollWidth - cw;
    const progress = maxScroll > 0 ? el.scrollLeft / maxScroll : 0;
    const activePage = Math.round(progress * (pageCount - 1));
    setPages(pageCount);
    setActive(Math.max(0, Math.min(pageCount - 1, activePage)));
  }, []);

  useLayoutEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, gallery.length]);

  // The open photo index lives in the URL hash (see file header). `undefined` =
  // closed. Opening PUSHES a hash entry (deep-link state + a harmless history
  // fallback); arrows REPLACE it; close clears the hash in place (replace).
  const lbMatch = /^#gphoto-(\d+)$/.exec(location.hash);
  const lbIndex = lbMatch ? Number(lbMatch[1]) : undefined;
  const goToPhoto = useCallback(
    (i: number, replace: boolean) =>
      navigate({ pathname: location.pathname, search: location.search, hash: `gphoto-${i}` }, { replace }),
    [navigate, location.pathname, location.search],
  );
  const openLightbox = useCallback((i: number) => goToPhoto(i, false), [goToPhoto]);
  const navigateLightbox = useCallback((i: number) => goToPhoto(i, true), [goToPhoto]);
  // Route-STABLE close: clear the hash in place and stay on the detail route.
  // The Telegram BackButton (owned by the open lightbox) drives this — we do NOT
  // depend on a history pop, because the device/TG back action isn't guaranteed
  // to behave like a router pop (it doesn't, on real iOS). The pushed hash entry
  // remains only as deep-link / reload state + a harmless history fallback.
  const closeLightbox = useCallback(() => {
    navigate({ pathname: location.pathname, search: location.search, hash: '' }, { replace: true });
  }, [navigate, location.pathname, location.search]);

  // While the lightbox is open, intercept a browser/WKWebView history POP (the
  // likely shape of the iOS Telegram "back") and close the viewer IN PLACE
  // instead of leaving the page. Only POP is blocked — our own open/close/arrow
  // navigations are PUSH/REPLACE and proceed normally (so no deadlock). This
  // complements the Telegram BackButton path (DetailLightbox) for the case where
  // the device back manifests as a router pop rather than `backButtonClicked`.
  const lightboxOpen = lbIndex !== undefined;
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ historyAction }) => lightboxOpen && historyAction === 'POP',
      [lightboxOpen],
    ),
  );
  useEffect(() => {
    if (blocker.state === 'blocked') {
      blocker.reset(); // cancel the POP — stay on the detail route
      closeLightbox(); // ...and close the viewer (clears the hash, REPLACE)
    }
  }, [blocker, closeLightbox]);

  if (gallery.length === 0) return null;
  return (
    <div className={styles.gallery} data-testid="detail-gallery">
      <div className={styles.galleryTrack} ref={trackRef} onScroll={measure} data-testid="gallery-track">
        {gallery.map((img, i) => {
          return (
            <button
              type="button"
              key={`${img.url}-${i}`}
              className={styles.shot}
              onClick={() => openLightbox(i)}
              aria-label={`View photo ${i + 1}`}
              data-testid="gallery-shot"
            >
              <FocalImage
                src={img.url}
                alt=""
                focalPointX={img.focalX}
                focalPointY={img.focalY}
                className={styles.shotImage}
                loading="lazy"
              />
            </button>
          );
        })}
      </div>
      {pages > 1 && (
        <div className={styles.dots} aria-hidden="true" data-testid="gallery-dots">
          {Array.from({ length: pages }, (_, i) => (
            <i
              key={`dot-${i}`}
              className={i === active ? styles.on : undefined}
              data-active={i === active ? 'true' : 'false'}
            />
          ))}
        </div>
      )}
      {lbIndex !== undefined && (
        <Lightbox
          images={gallery}
          index={Math.max(0, Math.min(gallery.length - 1, lbIndex))}
          onClose={closeLightbox}
          onNavigate={navigateLightbox}
        />
      )}
    </div>
  );
}
